import React, { Dispatch, SetStateAction } from 'react';
import {
  CardContainer,
  CardContent,
  CardFooter,
  CardFooterLeftContent,
  CardFooterRightContent,
  ReorderHandler,
} from '../atoms/styles';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight, SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import { useUser } from 'src/modules/shared';
import { Tooltip, WysiwygEditor } from 'src/modules/shared/components';
import theme from 'src/config/theme';
import {
  StudyCardsTypes,
  useGetStudyCardsQuery,
  useReorderStudyCardsMutation,
} from 'src/modules/shared/slices/study-card/api/studyCard';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import { Grid } from '@material-ui/core';
import { setCurrentPreviewCardIndex } from 'src/modules/shared/slices/study-card/entity/StudyCardSlice';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';
import { useGetPagesQuery } from 'src/modules/shared/slices/page/api/pages';
import { trajectorySectionCodeLookup } from 'src/config/api/models/Trajectory';

export const StudyCardList: React.FC<{
  type: StudyCardsTypes;
  onCardClick: Dispatch<SetStateAction<StudyCardsTypes | null>>;
}> = ({ type, onCardClick }) => {
  const dispatch = useAppDispatch();
  const log = useDataLogging();
  const [reorderStudyCards] = useReorderStudyCardsMutation();
  const { isTeacher, isAdmin, isAuthor, isSuperAdmin } = useUser();
  const shouldShowDetails = isAdmin || isAuthor || isSuperAdmin;
  const trajectoryId = useAppSelector((state) => state.trajectories.currentTrajectoryId);
  const viewMode = useAppSelector((state) => state.trajectories.viewMode);
  const { data: pageData } = useGetPagesQuery({
    trajectoryId: trajectoryId!,
    type: trajectorySectionCodeLookup['3'],
  });

  const pageId = pageData?.find((item) => item.type === trajectorySectionCodeLookup['3'])?.id;

  const { data } = useGetStudyCardsQuery(
    {
      cardType: type,
      pageId: pageId as string,
    },
    { skip: !pageId }
  );

  const handleCardClick = (index: number) => {
    if (data) {
      log(LogActionTypes.PREVIEW_CARD, {
        cardId: data[index].id,
      });
    }

    onCardClick(type);
    dispatch(setCurrentPreviewCardIndex(index));
  };

  const handleReorder = (cardId: string, position: number) => {
    if (!pageId) return;
    reorderStudyCards({ studyCardId: cardId, pageId, cardType: type, position });
  };

  if (data && data.length === 0) {
    return (
      <Grid item xs={12}>
        <p>Geen kaartjes in deze categorie.</p>
      </Grid>
    );
  }

  return (
    <>
      {data?.map((card, index) => (
        <Grid key={card.id} item xs={6} sm={4} lg={3} xl={2}>
          <span onClick={() => {}}>
            <CardContainer>
              <CardContent onClick={() => handleCardClick(index)}>
                <WysiwygEditor id={card?.id} value={card?.question} readOnly />
              </CardContent>
              <CardFooter>
                <>
                  <CardFooterLeftContent>
                    {index !== 0 && shouldShowDetails && viewMode === 'edit' && (
                      <>
                        <ReorderHandler onClick={() => handleReorder(card.id, index - 1)}>
                          <FontAwesomeSpriteIcon
                            icon={SpriteIconsSolid.CHEVRON_LEFT}
                            iconSize="md"
                            color={theme.palette.secondary.main}
                          />
                        </ReorderHandler>
                      </>
                    )}
                    {!isTeacher && (
                      <>
                        <Tooltip arrow placement="bottom" title="Bekeken">
                          <div>
                            <FontAwesomeSpriteIcon icon={SpriteIconsLight.EYE} color="secondary" />
                            <span>{card?.amountOfViews}</span>
                          </div>
                        </Tooltip>
                        <Tooltip arrow placement="bottom" title="Juist gemarkeerd">
                          <div>
                            <FontAwesomeSpriteIcon
                              icon={SpriteIconsSolid.CHECK}
                              color="secondary"
                            />
                            <span>{card?.amountOfCorrectAttempts?.toString()}</span>
                          </div>
                        </Tooltip>
                      </>
                    )}
                  </CardFooterLeftContent>
                  <CardFooterRightContent>
                    {card?.isKnown && (
                      <Tooltip arrow placement="bottom" title="Gekend (3 keer juist na elkaar)">
                        <div className="is-known">
                          <FontAwesomeSpriteIcon
                            icon={SpriteIconsSolid.CHECK}
                            color="white"
                            iconSize="2xs"
                          />
                        </div>
                      </Tooltip>
                    )}
                    {index !== data.length - 1 && shouldShowDetails && viewMode === 'edit' && (
                      <>
                        <ReorderHandler onClick={() => handleReorder(card.id, index + 1)}>
                          <FontAwesomeSpriteIcon
                            icon={SpriteIconsSolid.CHEVRON_RIGHT}
                            iconSize="md"
                            color={theme.palette.secondary.main}
                          />
                        </ReorderHandler>
                      </>
                    )}
                  </CardFooterRightContent>
                </>
              </CardFooter>
            </CardContainer>
          </span>
        </Grid>
      ))}
    </>
  );
};
